import { useEffect } from 'react';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import CapsuleAccordion from 'src/app/components/Elements/Accordion';
import Spinner from 'src/app/components/Elements/Spinner';
import { Typography } from 'src/app/components/Elements/Typography';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import type { RenderedAccordionQuestions } from 'src/app/types';

import { PartnerLogo } from './styles';

export interface EmployeeBenefitsPageProps {
  isAccountDataFetched: boolean;
  isUserPartOfCapsuleAtWork: boolean;
  redirectHome: () => void;
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => void;
}

const questions = [
  {
    question: '10% exclusive employee discount on over-the-counter medications',
    answer: (
      <p>
        Save money when you order pharmacy essentials like Advil, Claritin, and other over-the-counter medications. Once
        you sign up, visit the Essentials page to place your order. Your 10% discount will be automatically applied at
        checkout.
      </p>
    ),
  },
  {
    question: 'Call or text pharmacists on a dedicated employee support line',
    answer: (
      <>
        <p>
          Text, call, email, or chat with your pharmacist whenever is convenient for you. Use your dedicated Morgan
          Stanley customer support line: Call or text: (646) 362-3092
        </p>
        <br />
        <p>
          Email: <a href="mailto:morganstanley@capsule.com">morganstanley@capsule.com</a>
        </p>
      </>
    ),
  },
  {
    question: 'Free same-day delivery',
    answer: (
      <p>
        We deliver your medications to your home or office, or wherever you are. Our delivery is free so you pay your
        copay or coinsurance. We will always let you know how much your prescription costs before you schedule your your
        delivery.
      </p>
    ),
  },
  {
    question: 'Your insurance accepted',
    answer: (
      <p>
        Capsule is in-network with Morgan Stanley’s medical plans through Cigna and UnitedHealthcare. We can deliver all
        of your prescriptions, including 90-day maintenance medications. On a different plan? No problem. We work with
        most major insurance plans.
      </p>
    ),
  },
];

const EmployeeBenefitsPage = ({
  isAccountDataFetched,
  isUserPartOfCapsuleAtWork,
  redirectHome,
}: EmployeeBenefitsPageProps) => {
  useEffect(() => {
    if (isAccountDataFetched && !isUserPartOfCapsuleAtWork) {
      redirectHome();
    }
  });

  useSegmentTrack(UNIFIED.viewEmployeeBenefits);

  const [trackExpandFaq] = useLazySegmentTrack(UNIFIED.expandFaq);

  const handleExpand = (questions: RenderedAccordionQuestions, expandedIndex: [number]) => {
    /** The text inside a fragment */
    // Expanded indexes will return an empty array when all accordions are collapsed
    if (expandedIndex?.length) {
      const text = questions[Number(expandedIndex[0])].answer.props.children;
      trackExpandFaq({ text });
    }
  };

  if (!isAccountDataFetched) return <Spinner centered />;

  return (
    <div>
      <PartnerLogo src="/public/morganstanley-dark.png" alt="Morgan Stanley Partnership Image" />
      <Typography marginBottom={'s5'} variant="h1">
        Explore your benefits
      </Typography>

      <CapsuleAccordion
        expandFirstQuestion={true}
        name="employee-benefits-faq"
        questions={questions}
        wrapperClassName={'py-4'}
        whiteAccordion={true}
        expandCallback={handleExpand}
      />
    </div>
  );
};

export default EmployeeBenefitsPage;
